import { useCallback, useEffect, useState } from 'react';
import { superAiApi } from '../api';
import type { AITicket, TicketStatus } from '../types';

interface Props {
  isOwner: boolean;
}

const statusConfig: Record<TicketStatus, { label: string; className: string }> = {
  WAITING_OWNER: { label: 'Ожидает ответа', className: 'text-amber-400 border-amber-400/40 bg-amber-400/10' },
  OWNER_ANSWERED: { label: 'Отвечено', className: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/10' },
  CLOSED: { label: 'Закрыто', className: 'text-slate-500 border-slate-600 bg-slate-800/40' },
};

export const AiTicketPanel = ({ isOwner }: Props) => {
  const [tickets, setTickets] = useState<AITicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [selectedTicket, setSelectedTicket] = useState<AITicket | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [isAnswering, setIsAnswering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await superAiApi.listTickets(activeFilter || undefined);
      setTickets(data);
    } catch {
      setError('Ошибка загрузки AI-обращений');
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => { void load(); }, [load]);

  const handleAnswer = async () => {
    if (!selectedTicket || !answerText.trim()) return;
    setIsAnswering(true);
    try {
      const updated = await superAiApi.answerTicket(selectedTicket.id, answerText.trim());
      setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setSelectedTicket(updated);
      setAnswerText('');
    } catch {
      setError('Ошибка при ответе на AI-обращение');
    } finally {
      setIsAnswering(false);
    }
  };

  const filters: { label: string; value: string }[] = [
    { label: 'Все', value: '' },
    { label: 'Ожидают', value: 'WAITING_OWNER' },
    { label: 'Отвечено', value: 'OWNER_ANSWERED' },
    { label: 'Закрытые', value: 'CLOSED' },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between px-6 py-5 border-b border-slate-800 shrink-0">
        <div>
          <h3 className="text-lg font-black text-white">AI-обращения</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Запросы, на которые AI не нашёл ответ в базе знаний
          </p>
        </div>
        <div className="flex gap-1.5">
          {filters.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setActiveFilter(f.value)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition ${
                activeFilter === f.value
                  ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'
                  : 'border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-80 border-r border-slate-800 flex flex-col overflow-y-auto shrink-0">
          {loading && (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm animate-pulse">
              Загрузка...
            </div>
          )}
          {!loading && tickets.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-slate-600 text-sm text-center px-6">
              Нет AI-обращений
              {activeFilter && ` со статусом "${filters.find((f) => f.value === activeFilter)?.label}"`}
            </div>
          )}
          {!loading && tickets.map((ticket) => {
            const cfg = statusConfig[ticket.status];
            return (
              <button
                key={ticket.id}
                type="button"
                onClick={() => setSelectedTicket(ticket)}
                className={`text-left px-4 py-4 border-b border-slate-800/60 hover:bg-slate-900/60 transition ${
                  selectedTicket?.id === ticket.id ? 'bg-slate-900/80 border-l-2 border-l-cyan-500' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">
                    {ticket.ticket_code}
                  </span>
                  <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border ${cfg.className}`}>
                    {cfg.label}
                  </span>
                </div>
                <p className="text-sm text-slate-200 font-medium line-clamp-2 leading-snug">{ticket.question}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[11px] text-slate-500">{ticket.origin_user_name}</span>
                  <span className="text-[11px] text-slate-600">·</span>
                  <span className="text-[11px] text-slate-600">{ticket.origin_role}</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col">
          {!selectedTicket && (
            <div className="flex-1 flex items-center justify-center text-slate-600 text-sm">
              Выберите AI-обращение
            </div>
          )}
          {selectedTicket && (
            <div className="px-8 py-6 flex flex-col gap-6 flex-1">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">
                    {selectedTicket.ticket_code}
                  </span>
                  <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${statusConfig[selectedTicket.status].className}`}>
                    {statusConfig[selectedTicket.status].label}
                  </span>
                </div>
                <h4 className="text-xl font-black text-white mb-2">{selectedTicket.question}</h4>
                <div className="text-xs text-slate-500 flex gap-4">
                  <span>Автор: <span className="text-slate-300">{selectedTicket.origin_user_name}</span></span>
                  <span>Роль: <span className="text-slate-300">{selectedTicket.origin_role}</span></span>
                  <span>{new Date(selectedTicket.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800">
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2">Объяснение AI</p>
                <p className="text-sm text-slate-300 leading-relaxed">{selectedTicket.explanation}</p>
              </div>

              {selectedTicket.owner_answer && (
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                  <p className="text-[11px] font-black uppercase tracking-widest text-emerald-400 mb-2">Ответ владельца системы</p>
                  <p className="text-sm text-emerald-100 leading-relaxed">{selectedTicket.owner_answer}</p>
                  {selectedTicket.answered_at && (
                    <p className="text-[11px] text-emerald-700 mt-2">
                      {new Date(selectedTicket.answered_at).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
              )}

              {isOwner && selectedTicket.status === 'WAITING_OWNER' && (
                <div className="flex flex-col gap-3">
                  <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">Ответить</p>
                  <textarea
                    rows={5}
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    placeholder="Введите ответ. После отправки он будет показан пользователю в его AI-переписке."
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-cyan-500 resize-none transition"
                  />
                  <button
                    type="button"
                    disabled={isAnswering || !answerText.trim()}
                    onClick={() => void handleAnswer()}
                    className="self-start px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 font-black text-sm transition"
                  >
                    Отправить ответ
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="shrink-0 px-6 py-3 bg-rose-500/10 border-t border-rose-500/30 text-sm text-rose-400 flex items-center justify-between">
          {error}
          <button type="button" onClick={() => setError(null)} className="text-rose-500 hover:text-rose-300">✕</button>
        </div>
      )}
    </div>
  );
};
